/**
 * Installation guide content for the Developer section, one entry per tab.
 * Sourced from the partner-facing Google Docs for each plugin (ENG-31365) and
 * checked against the plugin source in heimdall/tuif; update here when either
 * changes.
 *
 * The embeddable plugins (trade-in, resale, repairs) share one shape, built
 * from the helpers below so the pages stay aligned: the same "Before you
 * start" list, then Add the plugin script → Add the plugin element → plugin-
 * specific steps, then Troubleshooting and support with a CSP example.
 */
import { DashboardResources } from '@src/types';
import type { GuideBlock, GuideSection, PluginGuide } from './types';

const API_KEY_PLACEHOLDER = 'YOUR_TROVE_API_KEY';

const SUPPORT_TEXT =
  'For any other questions about installation or support, reach out to your Trove Program Manager.';

/** The shared "Before you start" list for an embeddable plugin. */
const pluginPrerequisites = (program: string) => [
  `An active ${program} program with Trove`,
  'Your Trove-provided API key',
  'Access to edit your website\'s HTML, so you control where the plugin appears',
  'A Content Security Policy that allows Trove\'s domains (see [Troubleshooting and support](#support))',
  'Basic familiarity with HTML, CSS and JavaScript',
];

const scriptStep = ({ production, uat, pages }: { production: string, uat: string, pages: string }): GuideSection => ({
  id: 'add-script',
  title: 'Add the plugin script',
  step: true,
  blocks: [
    {
      kind: 'text',
      text: 'Add the plugin script just before the closing `</body>` tag so it doesn\'t delay page load.',
    },
    {
      kind: 'code',
      language: 'html',
      code: {
        production: `<script src="${production}"></script>`,
        uat: `<script src="${uat}"></script>`,
      },
    },
    { kind: 'callout', tone: 'info', title: 'Which pages need this?', text: pages },
  ],
});

const elementStep = ({ intro, code, attributes }: {
  intro: string;
  code: string;
  attributes: string[][];
}): GuideSection => ({
  id: 'add-element',
  title: 'Add the plugin element',
  step: true,
  blocks: [
    { kind: 'text', text: intro },
    { kind: 'code', language: 'html', code },
    { kind: 'table', columns: ['Attribute', 'Required', 'Description'], rows: attributes },
  ],
});

const API_KEY_ATTRIBUTE = ['`apiKey`', 'Yes', 'Your Trove-provided API key.'];

/** Openers work identically for the trade-in and repairs plugins. */
const openerStep = (tag: string, pluginName: string): GuideSection => ({
  id: 'add-opener',
  title: 'Add openers',
  step: true,
  blocks: [
    {
      kind: 'text',
      text:
        `Place a \`<${tag}>\` element wherever customers should be able to open the ${pluginName} plugin.`,
    },
    { kind: 'code', language: 'html', code: `<${tag} variant="button"></${tag}>` },
    {
      kind: 'table',
      columns: ['Attribute', 'Required', 'Description'],
      rows: [
        [
          '`variant`',
          'Yes',
          'Which opener style to use. Plugins come with `button` and `link`; variants can be customized, ' +
            'renamed or removed, and new ones added, in plugin configuration.',
        ],
        ['`destination`', 'No', 'Opens the plugin to a specific screen. See [Deep link to a style](#deep-link).'],
      ],
    },
    {
      kind: 'text',
      text:
        'Opener text and styling come from plugin configuration, as part of each variant — anything placed ' +
        'inside the element is not shown. Ask your Trove Program Manager to change them.',
    },
    {
      kind: 'callout',
      tone: 'warning',
      text: 'An opener only works on pages that also include the plugin script and plugin element.',
    },
  ],
});

/** Deep links into the trade-in and repairs plugins; both resolve `trove://<plugin>/add-item`. */
const deepLinkStep = ({ tag, publicName, example, extraRows = [] }: {
  tag: string;
  publicName: string;
  example: string;
  extraRows?: string[][];
}): GuideSection => ({
  id: 'deep-link',
  title: 'Deep link to a style',
  step: true,
  blocks: [
    {
      kind: 'text',
      text:
        'Add a `destination` to an opener to skip straight to a product — for example, beside each item ' +
        'in a customer\'s order history. Replace `STYLE_NUMBER` with the item\'s style number.',
    },
    {
      kind: 'code',
      language: 'html',
      code: `<${tag}\n  variant="button"\n  destination="trove://${publicName}/add-item?style=STYLE_NUMBER"\n></${tag}>`,
      caption: example,
    },
    { kind: 'subheading', text: 'Supported destinations' },
    {
      kind: 'table',
      columns: ['Destination', 'Opens to'],
      rows: [
        [`\`trove://${publicName}/add-item?style=STYLE_NUMBER\``, 'Search results for that style number'],
        [`\`trove://${publicName}/add-item\``, 'An empty style number search'],
        [
          `\`trove://${publicName}/add-item?style=unknown\``,
          'Manual add, for customers who don\'t know their style number',
        ],
        ...extraRows,
      ],
    },
    {
      kind: 'text',
      text:
        'If a destination isn\'t supported, the plugin logs an error to the browser console and opens ' +
        'without navigating — for example, back to an order the customer already has in progress.',
    },
  ],
});

/** One Content Security Policy directive a plugin needs: [directive, sources, what they're for]. */
type CspDirective = [string, string[], string];

const supportSection = (directives: CspDirective[]): GuideSection => ({
  id: 'support',
  title: 'Troubleshooting and support',
  blocks: [
    {
      kind: 'callout',
      tone: 'info',
      title: 'Plugin not rendering?',
      text:
        'Rendering errors are most often Content Security Policy collisions. If your site sets a policy, ' +
        'add the sources below to it — a site without a policy needs no changes.',
    },
    {
      kind: 'table',
      columns: ['Directive', 'Add these sources', 'Used for'],
      rows: directives.map(([directive, sources, purpose]) => [
        `\`${directive}\``,
        sources.map(source => `\`${source}\``).join(' '),
        purpose,
      ]),
    },
    {
      kind: 'code',
      language: 'html',
      caption: 'Example policy',
      code: `<meta http-equiv="Content-Security-Policy" content="${
        directives.map(([directive, sources]) => `${directive} 'self' ${sources.join(' ')}`).join('; ')
      }">`,
    },
    {
      kind: 'text',
      text: 'The plugin also shows product images, so `img-src` must allow wherever your product images are hosted.',
    },
    { kind: 'text', text: SUPPORT_TEXT },
  ],
});

/**
 * Hosts the trade-in and repairs plugins load from (checked against heimdall/tuif): the script and
 * plugin config on *.trovesite.com, APIs on *.trove.com (api., upload.api.), the Material Symbols
 * icon font from Google Fonts, and Heap's hidden analytics iframe on tuif-plugin-*.trovesite.com.
 */
const TUIF_CSP_DIRECTIVES: CspDirective[] = [
  ['script-src', ['*.trovesite.com'], 'The plugin script'],
  ['connect-src', ['*.trove.com', '*.trovesite.com'], 'Trove APIs and plugin configuration'],
  ['style-src', ['fonts.googleapis.com'], 'The plugin\'s icon font stylesheet'],
  ['font-src', ['fonts.gstatic.com'], 'The plugin\'s icon font files'],
  ['frame-src', ['*.trovesite.com'], 'A hidden analytics frame, when Trove analytics is enabled'],
];

/**
 * Hosts the resale plugin loads from: the script and plugin config on *.trovesite.com, APIs on
 * *.trove.com (partner-suite-api. for products, api. for partner data), and its carousel arrow
 * icons from Cloudinary.
 */
const RESALE_CSP_DIRECTIVES: CspDirective[] = [
  ['script-src', ['*.trovesite.com'], 'The plugin script'],
  ['connect-src', ['*.trove.com', '*.trovesite.com'], 'Trove APIs and plugin configuration'],
  ['img-src', ['res.cloudinary.com'], 'The plugin\'s carousel arrow icons'],
];

const eventsCallout = (pluginName: string): GuideBlock => ({
  kind: 'callout',
  tone: 'info',
  title: 'Tracking plugin events',
  text:
    `On Shopify, ${pluginName} plugin events can flow into your analytics — see the ` +
    `[Google Tag Manager](/${DashboardResources.GtmGuide}) guide.`,
});

export const tradeinGuide: PluginGuide = {
  title: 'Trade-in plugin',
  summary:
    'Let customers check trade-in value, start a trade-in and receive a shipping label directly from your ' +
    'website — including deep links from their past purchases.',
  prerequisites: pluginPrerequisites('trade-in'),
  sections: [
    scriptStep({
      production: 'https://tuif-plugin-tradein.production.trovesite.com/plugin-tradein.js',
      uat: 'https://tuif-plugin-tradein.sandbox.trovesite.com/plugin-tradein.js',
      pages: 'Every page that shows a trade-in opener, and every page where the minimized plugin should stay visible.',
    }),
    elementStep({
      intro: 'Right after the script, add the `<trove-plugin-tradein>` element with your API key.',
      code: `<trove-plugin-tradein apiKey="${API_KEY_PLACEHOLDER}"></trove-plugin-tradein>`,
      attributes: [API_KEY_ATTRIBUTE],
    }),
    openerStep('trove-tradein-opener', 'trade-in'),
    deepLinkStep({
      tag: 'trove-tradein-opener',
      publicName: 'tradein',
      example: 'Trade in a past purchase',
      extraRows: [['`trove://tradein/cart`', 'The customer\'s trade-in cart']],
    }),
    {
      id: 'events',
      title: 'Track plugin events',
      blocks: [eventsCallout('trade-in')],
    },
    supportSection(TUIF_CSP_DIRECTIVES),
  ],
};

export const resaleGuide: PluginGuide = {
  title: 'Resale plugin',
  summary:
    'Show customers resale inventory for the product they\'re viewing, and let them buy it, directly from ' +
    'your product detail pages (PDPs).',
  prerequisites: pluginPrerequisites('resale'),
  sections: [
    scriptStep({
      production: 'https://widget-merchandising.production.trovesite.com/widget-merchandising.js',
      uat: 'https://widget-merchandising.sandbox.trovesite.com/widget-merchandising.js',
      pages: 'Every product detail page that shows the resale plugin.',
    }),
    elementStep({
      intro:
        'On each product detail page, add the `<widget-merchandising>` element where the plugin should appear. ' +
        'Fill in the product\'s parent SKU and, when known, the selected size and color.',
      code: [
        '<widget-merchandising',
        '  id="trove-merchandising-widget"',
        `  apiKey="${API_KEY_PLACEHOLDER}"`,
        '  parentSku="PARENT_SKU"',
        '  sizeCode="SIZE_CODE"',
        '  colorCode="COLOR_CODE"',
        '></widget-merchandising>',
      ].join('\n'),
      attributes: [
        API_KEY_ATTRIBUTE,
        ['`parentSku`', 'Yes', 'Parent SKU of the product on the page. Required on initial load.'],
        ['`sizeCode`', 'No', 'Currently selected size. Setting it improves product matching on initial load.'],
        ['`colorCode`', 'No', 'Currently selected color. Setting it improves product matching on initial load.'],
        ['`id`', 'No', 'Any id, so your code can find the element to update it (see the next step).'],
        ['`hideHeader`', 'No', 'Add this attribute (no value) to hide the plugin header.'],
        ['`hideSubheader`', 'No', 'Add this attribute (no value) to hide the plugin subheader.'],
      ],
    }),
    {
      id: 'update-attributes',
      title: 'Keep size and color in sync',
      step: true,
      blocks: [
        {
          kind: 'text',
          text:
            'When a shopper changes size or color on the PDP, update the matching attribute on the element. ' +
            'The plugin refreshes automatically — no other calls are needed.',
        },
        {
          kind: 'code',
          language: 'javascript',
          code: [
            'function updateColor(color) {',
            '  const widget = document.getElementById(\'trove-merchandising-widget\');',
            '  if (widget) {',
            '    widget.setAttribute(\'colorCode\', color);',
            '  }',
            '}',
            '',
            'function updateSize(size) {',
            '  const widget = document.getElementById(\'trove-merchandising-widget\');',
            '  if (widget) {',
            '    widget.setAttribute(\'sizeCode\', size);',
            '  }',
            '}',
          ].join('\n'),
        },
      ],
    },
    {
      id: 'events',
      title: 'Track plugin events',
      blocks: [
        {
          kind: 'text',
          text:
            'The plugin emits a `trove-widget-match-type` event whenever the match type changes. The event\'s ' +
            '`detail` is one of `perfect`, `imperfect`, `similar` or `no-results`.',
        },
        {
          kind: 'code',
          language: 'javascript',
          code: [
            'window.addEventListener(\'trove-widget-match-type\', (e) => {',
            '  console.log(`The match type is ${e.detail}`);',
            '});',
          ].join('\n'),
        },
      ],
    },
    supportSection(RESALE_CSP_DIRECTIVES),
  ],
};

export const repairGuide: PluginGuide = {
  title: 'Repairs plugin',
  summary:
    'Let customers start a repair directly from your website — including deep links from their past purchases.',
  prerequisites: pluginPrerequisites('repairs'),
  sections: [
    scriptStep({
      production: 'https://tuif-plugin-repair.production.trovesite.com/plugin-repair.js',
      uat: 'https://tuif-plugin-repair.sandbox.trovesite.com/plugin-repair.js',
      pages: 'Every page that shows a repair opener.',
    }),
    elementStep({
      intro: 'Right after the script, add the `<trove-plugin-repair>` element with your API key.',
      code: `<trove-plugin-repair apiKey="${API_KEY_PLACEHOLDER}"></trove-plugin-repair>`,
      attributes: [API_KEY_ATTRIBUTE],
    }),
    openerStep('trove-repair-opener', 'repairs'),
    deepLinkStep({ tag: 'trove-repair-opener', publicName: 'repair', example: 'Repair a past purchase' }),
    {
      id: 'events',
      title: 'Track plugin events',
      blocks: [eventsCallout('repairs')],
    },
    supportSection(TUIF_CSP_DIRECTIVES),
  ],
};

const GTM_PIXEL_CODE = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','YOUR GTM ID');

['Tradein Plugin', 'Repairs Plugin', 'Recycle Plugin'].forEach((pluginName) => {
  ['Open', 'Click', 'Navigation', 'Close'].forEach((actionName) => {
    analytics.subscribe(\`trove:\${pluginName} \${actionName}\`, (event) => {
      window.dataLayer.push({
        ...event.customData,
        event: \`trove_\${pluginName.replace(' ', '_').toLowerCase()}_\${actionName.toLowerCase()}\`,
      });
    });
  });
});`;

/** GTM event names per plugin for one action, e.g. trove_tradein_plugin_open. */
const eventNameRows = (action: string) => [
  ['Trade-in', `\`trove_tradein_plugin_${action}\``],
  ['Repair', `\`trove_repairs_plugin_${action}\``],
  ['Take-back', `\`trove_recycle_plugin_${action}\``],
];

const PROPERTY_COLUMNS = ['Property', 'Type', 'Required', 'Description'];

export const gtmGuide: PluginGuide = {
  title: 'Google Tag Manager',
  summary:
    'Send Trove plugin events to Shopify Analytics and on to Google Tag Manager. Covers the trade-in, ' +
    'repairs and take-back plugins.',
  prerequisites: [
    'Trove plugins installed on a Shopify storefront — Shopify Analytics isn\'t available on other platforms',
    'Shopify Analytics enabled as a destination in your plugin configuration (Trove sets this up — ask your Program Manager)',
    'Your Google Tag Manager container ID (`GTM-` followed by letters or numbers)',
    'Access to your store\'s settings in Shopify Admin',
  ],
  sections: [
    {
      id: 'add-pixel',
      title: 'Create a custom pixel in Shopify',
      step: true,
      blocks: [
        {
          kind: 'list',
          ordered: true,
          items: [
            'In Shopify Admin, open your storefront and click **Settings** (bottom left).',
            'Click **Customer events**, then **Add custom pixel**.',
            'Give the pixel a unique name, e.g. **Trove Plugins + GTM**, and click **Add pixel**.',
            'Complete the **Customer privacy** section of the pixel configuration.',
          ],
        },
      ],
    },
    {
      id: 'pixel-code',
      title: 'Paste the pixel code',
      step: true,
      blocks: [
        {
          kind: 'text',
          text:
            'Under **Code**, remove the starter code and paste the snippet below. Replace `YOUR GTM ID` with your ' +
            'Google Tag Manager container ID.',
        },
        { kind: 'code', language: 'javascript', code: GTM_PIXEL_CODE },
      ],
    },
    {
      id: 'connect',
      title: 'Save and connect',
      step: true,
      blocks: [
        {
          kind: 'list',
          ordered: true,
          items: [
            'Click **Save** at the top of the page.',
            'Click **Connect** in the pixel configuration header.',
            'Open one of your Trove plugins — events should start arriving in Google Tag Manager.',
          ],
        },
      ],
    },
    {
      id: 'common-properties',
      title: 'Common event properties',
      blocks: [
        { kind: 'text', text: 'Every event includes these properties. Most event types add more, listed below.' },
        {
          kind: 'table',
          columns: PROPERTY_COLUMNS,
          rows: [
            ['`event`', 'string', 'Yes', 'The event name (see each event type below).'],
            ['`gtm.uniqueEventId`', 'number', 'Yes', 'Unique event ID generated by GTM.'],
            ['`Partner Code`', 'string', 'Yes', 'Trove code for the brand partner using the plugin.'],
            ['`Plugin`', 'string', 'Yes', '`tradein`, `repair` or `recycle` (take-back).'],
            ['`Host Location`', 'string', 'Yes', 'Full URL of the page hosting the plugin.'],
            ['`Language`', 'string', 'Yes', 'The user\'s most preferred browser language.'],
            ['`Languages`', 'string', 'Yes', 'Comma-separated browser languages, most to least preferred.'],
          ],
        },
      ],
    },
    {
      id: 'open-events',
      title: 'Open events',
      blocks: [
        { kind: 'text', text: 'Fired when a plugin dialog is opened.' },
        { kind: 'table', columns: ['Plugin', 'GTM event name'], rows: eventNameRows('open') },
        {
          kind: 'table',
          columns: PROPERTY_COLUMNS,
          rows: [
            [
              '`Opener Destination`',
              'string',
              'No',
              'When the plugin opened to a deep-link destination, that `trove://` URI.',
            ],
            [
              '`Opener Variant`',
              'string',
              'No (but common)',
              'The `variant` of the opener used. Always present with out-of-the-box openers; absent when opened from JavaScript.',
            ],
          ],
        },
      ],
    },
    {
      id: 'click-events',
      title: 'Click events',
      blocks: [
        { kind: 'text', text: 'Fired when a call to action inside a plugin is clicked.' },
        { kind: 'table', columns: ['Plugin', 'GTM event name'], rows: eventNameRows('click') },
        {
          kind: 'table',
          columns: PROPERTY_COLUMNS,
          rows: [
            ['`Element Id`', 'string', 'Yes', 'Identifies the clicked call to action. Varies by plugin.'],
            ['`Product Title`', 'string', 'No', 'Set when the call to action concerns a specific product.'],
            [
              '`Style Number`',
              'string',
              'No',
              'Set when the call to action concerns a specific product or starts a style-number search.',
            ],
          ],
        },
      ],
    },
    {
      id: 'navigation-events',
      title: 'Navigation events',
      blocks: [
        {
          kind: 'text',
          text:
            'Fired when the user moves between screens inside a plugin. Carries a required `Screen` property ' +
            'naming the screen navigated to.',
        },
        { kind: 'table', columns: ['Plugin', 'GTM event name'], rows: eventNameRows('navigation') },
        { kind: 'subheading', text: 'Trade-in plugin screens' },
        {
          kind: 'table',
          columns: ['Screen', 'Description'],
          rows: [
            ['Cancel Warning', 'Warning shown when an action will cancel the order'],
            ['Cart', 'Cart'],
            ['Contact Info', 'Checkout contact info'],
            ['Item Condition', 'Confirm item condition while adding an item'],
            ['Item Eligibility', 'Confirm item eligibility while adding an item'],
            ['Item Loss Warning', 'Warning shown when an action will cancel the current item'],
            ['Manual Add', 'Look up an item by attributes instead of style number'],
            ['Review and Submit', 'Final checkout review'],
            ['Search', 'Find an item by style number'],
            ['Tradein Confirmed', 'Confirmation after the order is submitted'],
          ],
        },
        { kind: 'subheading', text: 'Repair plugin screens' },
        {
          kind: 'table',
          columns: ['Screen', 'Description'],
          rows: [
            ['Cancel Warning', 'Warning shown when an action will cancel the order'],
            ['Cart', 'Cart'],
            ['Contact Info', 'Checkout contact info'],
            ['Item Details', 'Add or edit an item'],
            ['Item Loss Warning', 'Warning shown when an action will cancel the current item'],
            ['Manual Add', 'Look up an item by attributes instead of style number'],
            ['Order Confirmed', 'Confirmation after the order is submitted'],
            ['Review and Submit', 'Final checkout review'],
            ['Search', 'Find an item by style number'],
          ],
        },
        { kind: 'subheading', text: 'Take-back plugin screens' },
        {
          kind: 'table',
          columns: ['Screen', 'Description'],
          rows: [
            ['Cancel Warning', 'Warning shown when an action will cancel the order'],
            ['Order Confirmed', 'Confirmation after the order is submitted'],
            ['Order Entry', 'Edit the order'],
            ['Review', 'Final checkout review'],
          ],
        },
      ],
    },
    {
      id: 'close-events',
      title: 'Close events',
      blocks: [
        { kind: 'text', text: 'Fired when the user closes a plugin. Carries only the common properties.' },
        { kind: 'table', columns: ['Plugin', 'GTM event name'], rows: eventNameRows('close') },
      ],
    },
    {
      id: 'support',
      title: 'Troubleshooting and support',
      blocks: [
        {
          kind: 'callout',
          tone: 'info',
          title: 'No events arriving?',
          text:
            'Check that the pixel is **Connected** in Shopify, that `YOUR GTM ID` was replaced, and that Trove ' +
            'has enabled Shopify Analytics for your plugins.',
        },
        { kind: 'text', text: SUPPORT_TEXT },
      ],
    },
  ],
};

/** Guide tabs in display order, after the API docs tab. */
export const PLUGIN_GUIDES: Array<{ resource: DashboardResources, label: string, guide: PluginGuide }> = [
  { resource: DashboardResources.TradeinPluginGuide, label: 'Trade-in plugin', guide: tradeinGuide },
  { resource: DashboardResources.ResalePluginGuide, label: 'Resale plugin', guide: resaleGuide },
  { resource: DashboardResources.RepairPluginGuide, label: 'Repairs plugin', guide: repairGuide },
  { resource: DashboardResources.GtmGuide, label: 'Google Tag Manager', guide: gtmGuide },
];
